import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type Money, getMoneyValue } from "@tosize/shapes/src/lib/utils";
import { ProductionUnit, cn, formatNumber } from "@tosize/ui";
import { Tabs } from "radix-ui";
import { Fragment, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { usePriceContext } from "../../context/PriceContext";

interface TabsTriggerProps {
	variant: "finishes" | "shapes" | "type";
	onValueChange?: (value: string) => void;
	value: string;
	title: string;
	description?: ReactNode;
	price?: Money;
	_priceFrom?: boolean;
	disabled?: boolean;
	image?: ReactNode;
	children?: ReactNode;
}

export function TabsTrigger(props: TabsTriggerProps) {
	const {
		onValueChange,
		value,
		image,
		title,
		description,
		price,
		_priceFrom = false,
		disabled = false,
		variant,
		children,
	} = props;

	const { isCheckModeCompletePanel } = usePriceContext();

	const intl = useIntl();

	return (
		<Tabs.Trigger
			onClick={onValueChange ? () => onValueChange(value) : undefined}
			disabled={disabled}
			value={value}
			className={cn(
				"rdx-state-active:border-success",
				"group relative flex items-start xs:items-center rounded-xl border-2 border-shades text-center last:child:mb-0",
				"focus:outline-none focus-visible:border-primary-medium motion-safe:transition-colors",
				"disabled:cursor-not-allowed disabled:text-shades-medium disabled:grayscale",
				{
					"child:mb-4 xs:flex-col xs:items-center p-4": variant === "type",
					"child:mb-2 p-4": variant === "shapes",
					"child:mb-1 p-2": variant === "finishes",
					"flex-col items-center justify-center": variant !== "type",
					"hover:border-primary-medium": !disabled,
				},
			)}
		>
			{(variant === "type" || (variant === "shapes" && value === "edit")) && (
				<div className="-right-3 -top-3 absolute hidden h-6 w-6 items-center justify-center rounded-full bg-success text-[16px] text-white leading-none group-rdx-state-active:flex">
					<FontAwesomeIcon icon={faCheck} />
				</div>
			)}

			{typeof image === "string" ? (
				<img
					src={image}
					alt={title}
					height={variant === "finishes" ? 36 : 62.5}
					width={variant === "finishes" ? 90 : 125}
					className={cn({
						"mr-2 xs:mr-0 max-w-[65px] xs:max-w-none [@media(max-width:374px)]:max-w-[45px]":
							variant === "type",
					})}
				/>
			) : (
				image
			)}

			<div
				className={cn("flex grow flex-col last:child:mb-0", {
					"child:mb-4 text-left xs:text-center": variant === "type",
					"child:mb-2": variant === "shapes",
					"child:mb-1": variant === "finishes",
				})}
			>
				<div
					className={cn({
						"font-bold": variant === "type",
						"leading-none": variant === "shapes",
						"text-sm": variant === "finishes",
					})}
				>
					{title}
				</div>

				{!!description && <div className="text-sm leading-tight">{description}</div>}

				{children}

				{price !== undefined && (
					<div className="mt-auto child:align-baseline text-sm leading-tight">
						{disabled ? (
							<span className="italic">
								<FormattedMessage defaultMessage="Optie niet beschikbaar" id="l6s4U7" />
							</span>
						) : (
							<Fragment>
								{/* {priceFrom && (
                  <>
                    <span className="text-shades-dark">
                      <FormattedMessage defaultMessage="vanaf" id="7GelsW" />
                    </span>
                    &nbsp;
                  </>
                )} */}
								<span className="font-bold text-xl">
									{formatNumber({
										lang: intl.locale,
										value: getMoneyValue(price),
										digits: { min: 2 },
									})}
								</span>
								&nbsp;
								<span>
									{isCheckModeCompletePanel && (
										<FormattedMessage defaultMessage="p/stuk" id="Ieu85Q" />
									)}
									{!isCheckModeCompletePanel && (
										<FormattedMessage
											defaultMessage="p/{unit}"
											id="1sEXZ0"
											values={{ unit: ProductionUnit.M2 }}
										/>
									)}
								</span>
								{price.display !== 2 && (
									<>
										<span className="lg:hidden 2xl:inline"> - </span>

										<span className="lg:block 2xl:inline">
											<FormattedMessage defaultMessage="excl. BTW" id="iZDVbs" />
										</span>
									</>
								)}
							</Fragment>
						)}
					</div>
				)}
			</div>
		</Tabs.Trigger>
	);
}
